import { locationApi } from '@/api/account-management/locationApi';
import { type LocationOnboardingStatus } from '@/models/account-management/OnboardingStatus';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { assignedOnly } from '../../permissions/locationScope';
import { useAssignment } from '../../permissions/useLocationScope';
import { fillHeight } from '../../theme';

// Backend section keys are PascalCase ("General", "Pos", "Payment"); prettify for display.
const sectionLabel = (section: string): string =>
    section === 'Pos' ? 'POS' : section === 'General' ? 'Details' : section;

// Locations still in client onboarding for the selected brand, with per-section completeness. The
// per-location tabbed onboarding form (reached via Continue) is a follow-on.
const Onboarding = (): ReactElement => {
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const navigate = useNavigate();

    const brandId = app.brandId ?? undefined;

    const { data: pending, isFetching: isLoading } = useQuery<LocationOnboardingStatus[]>({
        queryKey: ['onboarding-pending', brandId ?? ''],
        enabled: !!brandId,
        queryFn: () => locationApi.getOnboardingPending(brandId!),
        // Progress is computed server-side and changes as locations are onboarded — always refetch on
        // mount so the grid reflects current completeness. Drive the grid's loading overlay off isFetching
        // (above) so the refetch is visible instead of silently showing the previous (stale) list.
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
    });

    // The queue is per-location, so it has to be scoped like every other location list — it was listing the
    // brand's whole estate, including locations this user has no business onboarding (TTD-160). The row id IS
    // the location id, which is also what Continue navigates by.
    const { assigned, restrict } = useAssignment();
    const rows = restrict ? assignedOnly(pending, (row) => row.id, assigned) : pending;

    const handleContinue = (row: LocationOnboardingStatus) => {
        if (!row.slug) return;
        navigate(`/${app.organizationSlug}/configuration/onboarding/${row.slug}`);
    };

    const columns: GridColDef<LocationOnboardingStatus>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 1,
        },
        {
            field: 'sections',
            headerName: strings.onboardingProgress,
            flex: 2,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams<LocationOnboardingStatus>) => (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ height: '100%' }}>
                    {params.row.sections.map((s) => {
                        const complete = s.total > 0 && s.filled === s.total;
                        return (
                            <Chip
                                key={s.section}
                                size="small"
                                color={complete ? 'success' : 'warning'}
                                variant={complete ? 'filled' : 'outlined'}
                                label={`${sectionLabel(s.section)} ${s.filled}/${s.total}`}
                            />
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'actions',
            headerName: strings.actions,
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams<LocationOnboardingStatus>) => (
                <Button size="small" variant="outlined" onClick={() => handleContinue(params.row)}>
                    {strings.continue}
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <DataGrid
                    rows={rows ?? []}
                    columns={columns}
                    autoPageSize
                    // `rows` is undefined until the assignment is known, so keep the overlay up that long
                    // rather than showing an empty queue as if it were the answer.
                    loading={!brandId ? false : isLoading || (restrict && !assigned)}
                    localeText={{ noRowsLabel: !brandId ? strings.selectBrand : strings.onboardingNoPending }}
                    disableRowSelectionOnClick
                />
            </Box>
        </Box>
    );
};

export default Onboarding;
